import { useEffect, useState } from "react";
import {
    adminRestoreListR2,
    adminRestorePreview,
    adminRestoreExecute,
    adminOpsListBackups,
} from "@/lib/api";
import { toast } from "sonner";
import { DownloadCloud, HardDriveDownload, RefreshCw } from "lucide-react";

function formatBytes(n) {
    if (!n) return "—";
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRelative(iso) {
    if (!iso) return "never";
    try {
        const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (secs < 60) return `${secs}s ago`;
        if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
        if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
        return `${Math.floor(secs / 86400)} d ago`;
    } catch {
        return iso;
    }
}

// Restore-from-R2 admin card.
// Lists tarballs in the R2 bucket. Admin picks one → preview shows
// per-collection import counts → confirmation modal (RESTORE + password)
// → execute. Pure additive: skips docs whose natural key already exists.
export default function RestoreFromR2() {
    const [backups, setBackups] = useState(null);
    const [selectedKey, setSelectedKey] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);

    useEffect(() => { (async () => {
        try {
            const r = await adminRestoreListR2();
            setBackups(r);
        } catch { /* silent */ }
        try {
            const s = await adminOpsListBackups();
            setSyncStatus(s);
        } catch { /* silent */ }
    })(); }, []);

    const runPreview = async (key) => {
        setSelectedKey(key);
        setPreview(null);
        setPreviewLoading(true);
        try {
            const r = await adminRestorePreview(key);
            setPreview(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setPreviewLoading(false);
        }
    };

    const execute = async () => {
        setBusy(true);
        try {
            const r = await adminRestoreExecute({
                s3_key: selectedKey,
                confirm_text: confirmText,
                password,
            });
            setResult(r);
            toast.success(`Restored ${r.total_imported.toLocaleString()} documents`);
            setModalOpen(false);
            setConfirmText("");
            setPassword("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally {
            setBusy(false);
        }
    };

    const totalNew = preview
        ? preview.collections.reduce((s, c) => s + c.would_import, 0)
        : 0;
    const nonEmptyRows = preview
        ? preview.collections.filter((c) => c.in_tarball > 0)
        : [];

    return (
        <div className="bl-card p-5 mt-6" data-testid="admin-restore-card">
            <div className="flex items-start gap-3">
                <HardDriveDownload
                    className="w-5 h-5 text-bl-muted mt-0.5 shrink-0"
                    aria-hidden
                />
                <div className="flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-semibold">
                        Restore content from R2
                    </div>
                    <div className="mt-1 text-sm text-bl-muted">
                        Import curated content (articles, packages, images,
                        configs) from a Cloudflare R2 backup tarball.
                        <b className="text-bl"> Additive only</b> — skips docs
                        that already exist, never touches users / payments /
                        sessions / entitlements.
                    </div>
                </div>
            </div>

            {/* Cross-env auto-sync status banner */}
            {syncStatus && (
                <div
                    className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-bl-muted px-2 py-1.5 bg-bl-bg rounded border border-bl"
                    data-testid="admin-restore-sync-status"
                >
                    <span className="flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        <span className="text-[10px] tracking-[0.18em] uppercase">
                            Weekly cross-env sync
                        </span>
                    </span>
                    <span>
                        env=
                        <code className="text-bl">{syncStatus.env_label}</code>
                    </span>
                    <span>
                        {syncStatus.cross_env_sync_enabled ? (
                            <>
                                <span className="text-[var(--bl-moss)] font-semibold">ON</span>
                                {" · Sunday 08:00 UTC"}
                            </>
                        ) : (
                            <>
                                <span className="text-[var(--bl-amber)] font-semibold">OFF</span>
                                {" · set "}
                                <code>CROSS_ENV_SYNC_ENABLED=true</code>
                            </>
                        )}
                    </span>
                    {syncStatus.last_cross_env_sync && (
                        <span>
                            last:{" "}
                            <span
                                className={
                                    syncStatus.last_cross_env_sync.ok
                                        ? "text-[var(--bl-moss)]"
                                        : "text-[var(--bl-rust)]"
                                }
                            >
                                {syncStatus.last_cross_env_sync.ok ? "✓" : "!"}{" "}
                                {syncStatus.last_cross_env_sync.total_imported ?? 0}{" "}
                                docs · {formatRelative(syncStatus.last_cross_env_sync.started_at)}
                            </span>
                        </span>
                    )}
                </div>
            )}

            {/* Backup list */}
            {backups?.backups?.length > 0 ? (
                <ul
                    className="mt-4 space-y-1.5 max-h-40 overflow-auto"
                    data-testid="admin-restore-list"
                >
                    {backups.backups.slice(0, 8).map((b) => (
                        <li
                            key={b.key}
                            className={
                                "flex items-center justify-between gap-2 p-2 rounded border cursor-pointer transition-colors "
                                + (selectedKey === b.key
                                    ? "border-bl-primary bg-bl-teal-tint"
                                    : "border-bl hover:bg-bl-bg")
                            }
                            onClick={() => runPreview(b.key)}
                            data-testid={`admin-restore-row-${b.key}`}
                        >
                            <span className="flex items-center gap-2 min-w-0 flex-1">
                                <span
                                    className={
                                        "inline-block px-1.5 py-0.5 rounded text-[9px] uppercase shrink-0 "
                                        + (b.kind === "weekly"
                                            ? "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]"
                                            : b.kind === "predayzero"
                                                ? "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]"
                                                : "bg-bl-bg text-bl-muted")
                                    }
                                >
                                    {b.kind}
                                </span>
                                <span className="font-mono-bl text-bl text-[11px] truncate">
                                    {b.key}
                                </span>
                            </span>
                            <span className="text-[10px] text-bl-muted tnum shrink-0">
                                {formatBytes(b.size_bytes)}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="mt-4 text-xs text-bl-muted">
                    No R2 backups found. Run a backup first or check that{" "}
                    <code>BACKUP_S3_*</code> env vars are set.
                </p>
            )}

            {/* Preview */}
            {previewLoading && (
                <p className="mt-3 text-xs text-bl-muted">Reading tarball…</p>
            )}
            {preview && (
                <div className="mt-4 border border-bl rounded p-3" data-testid="admin-restore-preview">
                    <div className="flex items-baseline justify-between">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Would import
                        </div>
                        <div className="font-display font-semibold text-xl text-bl tnum">
                            {totalNew.toLocaleString()}{" "}
                            <span className="text-[10px] tracking-[0.18em] uppercase text-bl-muted ml-1">
                                new docs
                            </span>
                        </div>
                    </div>
                    <ul className="mt-2 text-[11px] space-y-0.5 max-h-44 overflow-auto">
                        {nonEmptyRows.map((r) => (
                            <li
                                key={r.collection}
                                className="grid grid-cols-[1fr_60px_60px_60px] gap-1.5"
                            >
                                <span className="font-mono-bl text-bl truncate">
                                    {r.collection}
                                </span>
                                <span className="text-right text-bl-muted tnum">
                                    {r.in_tarball}
                                </span>
                                <span className="text-right text-bl-muted tnum">
                                    {r.already_present}
                                </span>
                                <span
                                    className={
                                        "text-right tnum font-semibold "
                                        + (r.would_import > 0
                                            ? "text-[var(--bl-moss)]"
                                            : "text-bl-muted")
                                    }
                                >
                                    +{r.would_import}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="grid grid-cols-[1fr_60px_60px_60px] gap-1.5 mt-1 pt-1 border-t border-bl text-[9px] tracking-[0.15em] uppercase text-bl-muted">
                        <span></span>
                        <span className="text-right">In tar</span>
                        <span className="text-right">Present</span>
                        <span className="text-right">New</span>
                    </div>

                    <div className="mt-3 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setModalOpen(true)}
                            disabled={totalNew === 0}
                            className="bl-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                            data-testid="admin-restore-open-modal-btn"
                        >
                            <DownloadCloud className="w-3.5 h-3.5" />
                            {totalNew === 0
                                ? "Nothing new to import"
                                : `Import ${totalNew.toLocaleString()} docs →`}
                        </button>
                    </div>
                </div>
            )}

            {result && (
                <div
                    className="mt-3 p-3 bg-[var(--bl-moss-tint)] border border-[var(--bl-moss)] rounded text-xs text-bl"
                    data-testid="admin-restore-result"
                >
                    ✓ Restored <b>{result.total_imported.toLocaleString()}</b>{" "}
                    documents in {result.duration_seconds}s
                </div>
            )}

            {/* Confirmation modal */}
            {modalOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
                    data-testid="admin-restore-modal"
                >
                    <div className="bl-card p-6 max-w-md w-full border-2 border-bl-primary">
                        <div className="text-bl-primary font-display font-semibold text-lg">
                            Confirm content restore
                        </div>
                        <p className="mt-2 text-sm text-bl">
                            About to import <b>{totalNew.toLocaleString()}</b>{" "}
                            new documents from{" "}
                            <code className="text-[11px]">{selectedKey}</code>.
                            Existing docs will be skipped. Users / payments /
                            sessions will NOT be touched.
                        </p>
                        <label className="block mt-4 text-xs font-semibold text-bl">
                            Type{" "}
                            <code className="px-1.5 py-0.5 bg-bl-bg border border-bl rounded">
                                RESTORE
                            </code>{" "}
                            to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-bl rounded bg-bl-surface text-bl font-mono-bl"
                            placeholder="RESTORE"
                            data-testid="admin-restore-confirm-input"
                            autoComplete="off"
                        />
                        <label className="block mt-3 text-xs font-semibold text-bl">
                            Admin password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-bl rounded bg-bl-surface text-bl"
                            placeholder="••••••••"
                            data-testid="admin-restore-password-input"
                            autoComplete="current-password"
                        />
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="bl-btn-ghost text-sm"
                                disabled={busy}
                                data-testid="admin-restore-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={execute}
                                disabled={busy || confirmText !== "RESTORE" || !password}
                                className="bl-btn-primary text-sm disabled:opacity-40"
                                data-testid="admin-restore-execute-btn"
                            >
                                {busy ? "Restoring…" : "Execute restore"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
